"use client";

import { Button } from "@/components/ui/Button";
import { StatusCard } from "@/components/ui/StatusCard";
import { useQuizWizardController } from "./useQuizWizardController";
import { QuizAuthGate } from "./QuizAuthGate";
import { QuizStepPriorities } from "./QuizStepPriorities";
import { QuizStepConstraints } from "./QuizStepConstraints";
import { QuizStepReview } from "./QuizStepReview";
import {
  MIN_SPLASH_MS,
  QuizLoadingSplash,
  RESULTS_LOADING_MESSAGE,
  TURNSTILE_INTERACTIVE_MESSAGE,
} from "./QuizLoadingSplash";
import { preloadStepImage } from "./preloadStepImage";
import { TOTAL_QUIZ_STEPS } from "./quizConstants";
import { TOKEN_MAX_AGE_MS, useTurnstileToken } from "@/lib/turnstile/useTurnstileToken";
import { usePageResume } from "@/lib/hooks/usePageResume";
import { useState, useEffect, useRef } from "react";

function FieldErrorList({
  errors,
  heading,
}: {
  errors: Record<string, string[]>;
  heading?: string;
}) {
  const entries = Object.entries(errors).flatMap(([field, messages]) =>
    messages.map((msg) => {
      if (field === "non_field_errors" || field === "detail") return msg;
      const label = field
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return `${label}: ${msg}`;
    }),
  );
  if (entries.length === 0) return null;
  return (
    <div className="mt-2">
      {heading && <div className="text-sm font-medium text-text">{heading}</div>}
      <ul className="mt-1 list-disc pl-4 text-sm text-text-muted">
        {entries.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

export function QuizWizard() {
  // Hold the splash for MIN_SPLASH_MS so its message never flickers by on fast connections.
  const [minSplashDone, setMinSplashDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinSplashDone(true), MIN_SPLASH_MS);
    return () => clearTimeout(t);
  }, []);

  const {
    widget: turnstileWidget,
    getToken,
    reset: resetToken,
    interactive: turnstileInteractive,
  } = useTurnstileToken({
    surface: "quiz",
  });

  // After a bfcache restore or a long suspension (iOS app switch), the cached
  // Turnstile token is dead but no effect re-runs to notice. Clearing it is
  // always safe — the next gated call just fetches a fresh one. Deliberately
  // does NOT re-run init(): that would clobber in-progress answers; a dead
  // session surfaces lazily on the next submit, which recovers transparently.
  // minHiddenMs matches the token freshness window so a brief app switch
  // doesn't discard a token getToken() would still accept.
  usePageResume(resetToken, { minHiddenMs: TOKEN_MAX_AGE_MS });

  const {
    initState,
    step,
    stepImageOrders,
    trip,
    priorities,
    constraints,
    geoOptions,
    geoError,
    priorityDescriptions,
    busyOperation,
    submitting,
    error,
    errorKind,
    errorNonce,
    fieldErrors,
    missingRequired,
    completed,
    canContinue,
    inputsLocked,
    onNext,
    onSubmitSkipReview,
    onChangeTrip,
    onBack,
    onSubmit,
    onEdit,
    restartQuiz,
    retryLastSubmit,
    setPriorities,
    setConstraints,
  } = useQuizWizardController({ getToken, resetToken });

  // Each step swap is client-side state, not a route change, so the browser
  // never resets scroll on its own — do it here whenever the step changes.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);

  // The error card renders at the top of the form column while the
  // step's Next/submit button sits below the fold on a phone (the layout that
  // let the originating replay's user never actually see the card) — on a
  // new error, bring it into view and move focus onto it rather than relying
  // on the user to scroll up and find it themselves. errorNonce (not
  // error/errorKind) is the dependency: it bumps on every new error even when
  // the message and kind are identical to the last one (e.g. two consecutive
  // dropped-connection failures), which a value-equality dependency wouldn't
  // re-trigger on.
  //
  // This only works because the controller's error state
  // (which bumps errorNonce) and the "stop showing the branded splash"
  // state (submitting/busy → false) commit in the SAME React render — the
  // errorCard div doesn't exist in the DOM until that render lands.
  // That holds today because setQuizError and the various finally {
  // setBusy(null) / setSubmitting(false) } calls all run inside one async
  // function's continuation, which React 18+ batches automatically — but it
  // is a real coupling, not a guarantee this effect enforces itself. A
  // future change that splits those across renders (e.g. an intermediate
  // await, or moving one into a separate dispatch) would silently break
  // this without changing any type signature.
  const errorCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // errorNonce can only be 0 before any error has ever fired this mount —
    // setQuizError always bumps it together with setting `error` — so this
    // is defense-in-depth on top of (not a substitute for) the null check
    // below, which is what actually matters: it's what makes this effect
    // safe to fire on mount when there is nothing yet to focus.
    if (errorNonce === 0) return;
    const el = errorCardRef.current;
    if (!el) return;
    // scrollIntoView is unimplemented in jsdom — optional-call so tests don't
    // need to stub it just to render.
    const reducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView?.({ block: "center", behavior: reducedMotion ? "auto" : "smooth" });
    el.focus({ preventScroll: true });
  }, [errorNonce]);

  // Warm the priorities image as soon as its index is known — on mount,
  // i.e. during the splash — rather than waiting for step 1 to actually
  // render. QuizStepPriorities (and its <Image priority>) isn't mounted
  // until showSplash flips false, so without this the fetch can't start
  // until after the minimum splash (MIN_SPLASH_MS), guaranteeing a blur-up on every
  // fresh load.
  //
  // "auto", not "high": init() is still resolving while this runs (its own
  // anonymous-session/questionnaire-create/geography calls are what gate
  // the splash), and those should win the connection, not this image. The
  // ~3s head start is what removes the blur, not the priority hint. This
  // also means the fetch fires even on a resume that lands on step 2 (see
  // below) or on an init error — an accepted, low-priority cost, since
  // gating it on initState === "ready" would cost every fresh visitor the
  // head start just to save bandwidth for a returning minority.
  useEffect(() => {
    preloadStepImage(stepImageOrders.priorities[0]!, "auto");
  }, [stepImageOrders.priorities]);

  // Warm the constraints image once init() has resolved. It plays two
  // roles depending on where the user lands:
  //  - step === 1: it's simply the next step in the linear flow, so warm it
  //    at low priority while the user is still filling out step 1.
  //  - step === 2: init() resumed a returning visitor (one who already
  //    completed step 1 in a prior session) straight onto constraints, so
  //    it IS the LCP image — same as priorities normally is — and needs
  //    high priority. QuizStepShell also gets `imagePriority` for this
  //    case below, so its <Image> omits loading="lazy".
  // Gated on initState === "ready" so "high" only ever appears after
  // init()'s own network work has finished — never competing with it.
  useEffect(() => {
    if (initState !== "ready" || (step !== 1 && step !== 2)) return;
    preloadStepImage(stepImageOrders.constraints[0]!, step === 2 ? "high" : "low");
  }, [step, initState, stepImageOrders.constraints]);

  if (initState === "error") {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Keep the Turnstile widget mounted so Retry can trigger a challenge.
            Must stay visible (not display:none) — an escalated interactive
            checkbox would otherwise be unclickable and Retry would always
            time out. It renders nothing until restartQuiz() actually calls
            getToken(), since execution is "execute", not "render". */}
        {turnstileWidget}
        <StatusCard
          tone="error"
          title="Couldn't start the quiz"
          body={
            <>
              {error ?? "Unknown error"}
              {fieldErrors && <FieldErrorList errors={fieldErrors} />}
            </>
          }
          actions={
            <>
              <Button onClick={restartQuiz}>Retry</Button>
              <Button variant="secondary" href="/">
                Back home
              </Button>
            </>
          }
        />
      </div>
    );
  }

  if (initState === "auth_required") {
    return (
      <>
        {/* The splash backdrops the gate rather than the wizard shell — deliberately
            skips the 3s minSplashDone floor below (that only gates "ready"), since the
            gate should appear the instant the 403 lands, not three seconds later. */}
        <QuizLoadingSplash extraContent={turnstileWidget} />
        {/* onSuccess re-runs init() authenticated — the retry needs turnstileWidget
            still mounted above, since a cleared anon cookie (proxy behaviour on
            login/signup) means init() has to create a fresh anonymous session and may
            call getToken() again. */}
        <QuizAuthGate onSuccess={restartQuiz} />
      </>
    );
  }

  const showSplash =
    initState === "idle" ||
    initState === "loading" ||
    (initState === "ready" && !minSplashDone) ||
    submitting;

  if (showSplash) {
    // Once submitting, use the results-page phrase so the wording carries
    // straight through the navigation to results/loading.tsx with no flip.
    // extraContent is omitted while submitting: /complete isn't a Turnstile-gated
    // path, so there's nothing for the widget to do here, and re-mounting it on
    // this render (it was unmounted during steps 1-2) would otherwise trigger a
    // fresh, pointless challenge at the exact moment the user expects results.
    // When Cloudflare escalates to a visible checkbox, swap the whimsical phrase
    // for explicit instructions — the checkbox renders below the phrase and a
    // "Tracking your perfect safari..." message buries it.
    return (
      <QuizLoadingSplash
        message={
          submitting
            ? RESULTS_LOADING_MESSAGE
            : turnstileInteractive
              ? TURNSTILE_INTERACTIVE_MESSAGE
              : undefined
        }
        extraContent={submitting ? undefined : turnstileWidget}
      />
    );
  }

  // The action offered is matched to what the error actually needs,
  // not a single generic "try again" —
  //   - "retryable" (network blip / opaque 5xx / 429): re-dispatch the same
  //     operation. retryLastSubmit re-runs against CURRENT form state, not
  //     the payload that failed, so an edit made after the error is what
  //     actually gets (re)submitted. disabled={!canContinue}:
  //     onNext/onSubmitSkipReview themselves early-return when the
  //     form isn't currently submittable, so without this the button would
  //     silently no-op if the user, say, cleared a required field after the
  //     error — same guard the step's own Next button already gets.
  //   - "validation": the server rejected the payload — the field list below
  //     is the instruction; resubmitting the identical payload can't help,
  //     so there's deliberately no button.
  //   - "session_dead" / "no_instance": the session or instance itself is
  //     gone — nothing to retry, only a fresh quiz. Reuses `restartQuiz` (the same
  //     one the init-error StatusCard above uses), which re-runs init(). Not
  //     canContinue-gated — restartQuiz() doesn't depend on current form validity.
  const errorAction =
    errorKind === "retryable" ? (
      <Button onClick={retryLastSubmit} disabled={!canContinue}>
        Try again
      </Button>
    ) : errorKind === "session_dead" || errorKind === "no_instance" ? (
      <Button onClick={restartQuiz}>Start a new quiz</Button>
    ) : null;

  // errorCard is the bare content, used inline inside QuizStepShell's form
  // column on steps 1 & 2 so it consumes the column's own slack instead of
  // stacking above PhotoSplit's grid (which would force the page to
  // overflow — the same failure class the progress-bar fix addresses).
  // errorBanner wraps it as a standalone in-flow block for step 3, which
  // has its own shell and doesn't go through QuizStepShell.
  //
  // role="alert"/tabIndex/the scroll-focus ref all live on THIS element, not
  // on a QuizStepShell-level wrapper — QuizStepShell has no way to know when
  // banner's content actually changed vs. just re-rendered, but this
  // component does (errorNonce, above).
  const errorCard = error && !completed ? (
    <div
      ref={errorCardRef}
      role="alert"
      tabIndex={-1}
      // focus-ring-tight (the sanctioned utility — see
      // app/globals.css) is :focus-visible-only, and browser heuristics for
      // a script-triggered .focus() (as opposed to a keyboard interaction)
      // are inconsistent about matching it — the ring could silently not
      // render on the very focus move this component exists to make
      // noticeable. Third documented exception to the "exactly two ring
      // utilities" rule alongside form inputs' glow ring and .regions-*'s
      // outline (both called out in app/globals.css): this alert is reached
      // by scroll+focus, never by tabbing, so there's no mouse-vs-keyboard
      // distinction here for :focus-visible to usefully preserve.
      className="rounded-[var(--radius-lg)] focus:outline-none focus:ring-2 focus:ring-focus"
    >
      <StatusCard
        tone="error"
        title="There's a problem"
        body={
          <>
            {error}
            {fieldErrors && <FieldErrorList errors={fieldErrors} heading="Please fix:" />}
            {missingRequired && (
              <FieldErrorList errors={missingRequired} heading="Missing required:" />
            )}
          </>
        }
        actions={errorAction}
      />
    </div>
  ) : null;

  const errorBanner = errorCard ? <div className="px-4 py-3 sm:px-6">{errorCard}</div> : null;

  const progressPct = step <= TOTAL_QUIZ_STEPS ? Math.round((step / TOTAL_QUIZ_STEPS) * 100) : 100;
  const progressBar = step <= TOTAL_QUIZ_STEPS ? (
    <div
      className="fixed inset-x-0 top-[var(--bsa-nav-h)] z-10 bg-bush-100"
      style={{ height: 3 }}
    >
      <div
        className="h-full bg-primary"
        style={{
          width: `${progressPct}%`,
          transition: "width 0.35s ease",
        }}
      />
    </div>
  ) : null;

  if (step === 1) {
    return (
      <>
        {progressBar}
        <QuizStepPriorities
          value={priorities}
          onChange={setPriorities}
          onNext={onNext}
          onBack={onBack}
          nextDisabled={!canContinue}
          disabled={inputsLocked}
          imageOrder={stepImageOrders.priorities}
          imagePriority
          banner={errorCard}
          descriptions={priorityDescriptions}
        />
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        {progressBar}
        <QuizStepConstraints
          value={constraints}
          trip={trip}
          onChange={setConstraints}
          onNext={onNext}
          onSubmitSkipReview={onSubmitSkipReview}
          onBack={onBack}
          onChangeTrip={onChangeTrip}
          nextDisabled={!canContinue}
          disabled={inputsLocked}
          imageOrder={stepImageOrders.constraints}
          imagePriority
          geoOptions={geoOptions}
          geoError={geoError}
          banner={errorCard}
        />
      </>
    );
  }

  return (
    <>
      {errorBanner}
      <QuizStepReview
        trip={trip}
        priorities={priorities}
        constraints={constraints}
        onEdit={onEdit}
        onBack={onBack}
        onSubmit={onSubmit}
        submitBusy={busyOperation === "complete"}
        disabled={inputsLocked}
        geoOptions={geoOptions}
      />
    </>
  );
}
